import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

function shorten(val) {
  if (!val || typeof val !== "string") return "-";
  const s = val.trim();
  if (s.length <= 14) return s;
  return `${s.slice(0, 6)}…${s.slice(-6)}`;
}

export default function PropertyCardsList({
  rows,
  isFamilyMember,
  detectBlockchain,
  onToggleChildren,
  isChildrenExpanded,
  renderParentActions,
  renderChildActions,
}) {
  const [expanded, setExpanded] = useState({});

  if (!rows || rows.length === 0) return null;

  return (
    <Stack
      spacing={2}
      sx={{
        px: 2,
        py: 2,
        width: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {rows.map((row) => {
        const isExpandedRow = Boolean(isChildrenExpanded?.(row));
        const childCount = Array.isArray(row.children) ? row.children.length : 0;

        return (
          <Card
            key={row._id}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              overflowX: "hidden",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    "@media (max-width:450px)": {
                      flexDirection: "column",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={row.imgUrl}
                    alt="Asset"
                    sx={{
                      width: 92,
                      height: 68,
                      borderRadius: 1.5,
                      objectFit: "cover",
                      flex: "0 0 auto",
                      bgcolor: "background.default",
                      "@media (max-width:450px)": {
                        width: "100%",
                        height: 140,
                      },
                    }}
                  />

                  <Stack sx={{ "@media (max-width:450px)": { width: "100%" }, minWidth: 0, flex: 1 }} spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} paddingTop={{xs:1,sm:0}} alignItems="flex-start">
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        title={row.propertyName}
                      >
                        {row.propertyName}
                      </Typography>
                      <Box sx={{ flex: "0 0 auto" }}>{renderParentActions?.(row)}</Box>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body3" sx={{ color: "text.secondary" }}>
                        Mode
                      </Typography>
                      <Typography
                        variant="body3"
                        sx={{
                          color: "text.secondary",
                          textAlign: "right",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        title={row.rightToOwnerShip}
                      >
                        {row.rightToOwnerShip}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body3" sx={{ color: "text.secondary" }}>
                        Ownership
                      </Typography>
                      <Typography
                        variant="body3"
                        sx={{
                          color: "text.secondary",
                          textAlign: "right",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        title={row.percentInvested}
                      >
                        {row.percentInvested}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="label">Total Tokens</Typography>
                    <Typography variant="body3" sx={{ textAlign: "right" }}>
                      {row.frax
                        ? `${row.frax}-${row.tokenName ? row.tokenName.toUpperCase() : "FRAX"}`
                        : "0"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="label">Total Value</Typography>
                    <Typography variant="body3" sx={{ textAlign: "right" }}>
                      {row.investAmt || "-"}
                    </Typography>
                  </Stack>
                  {isFamilyMember && (
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="label">Investor</Typography>
                      <Typography variant="body3" sx={{ textAlign: "right" }}>
                        {row.username ? `${row.username}(${row.relation})` : "Family"}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {childCount > 0 && (
                  <>
                    <Divider />
                    <Button
                      variant="text"
                      size="small"
                      sx={{ textTransform: "none", px: 0, alignSelf: "flex-start" }}
                      onClick={() => onToggleChildren?.(row)}
                    >
                      {isExpandedRow
                        ? `Hide transactions (${childCount})`
                        : `View transactions (${childCount})`}
                    </Button>

                    {isExpandedRow && (
                      <Stack spacing={1.25}>
                        {row.children.map((child) => (
                          <Card
                            key={child.id || child._id}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              width: "100%",
                              overflowX: "hidden",
                              boxSizing: "border-box",
                              bgcolor: "background.default",
                            }}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="label">Blockchain</Typography>
                                  <Typography variant="body3" sx={{ textAlign: "right" }}>
                                    {detectBlockchain?.(child.dltAccount)}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="label">Account</Typography>
                                  <Typography
                                    variant="body3"
                                    sx={{
                                      textAlign: "right",
                                      fontFamily: "monospace",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: 180,
                                    }}
                                    title={child.dltAccount}
                                  >
                                    {shorten(child.dltAccount)}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="label">Tokens</Typography>
                                  <Typography variant="body3" sx={{ textAlign: "right" }}>
                                    {(child.frax ?? child.numberOfFraxTransfered ?? 0)}{" "}
                                    {(child.tokenName || row.tokenName || "FRAX").toUpperCase()}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="label">Ownership</Typography>
                                  <Typography variant="body3" sx={{ textAlign: "right" }}>
                                    {typeof child.percentage === "number"
                                      ? `${child.percentage.toFixed(2)}%`
                                      : `${Number(child.percentage || 0).toFixed(2)}%`}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                  <Typography variant="label">Amount</Typography>
                                  <Typography variant="body3" sx={{ textAlign: "right" }}>
                                    {child.amoutPaid ||
                                      (child.amtPaidForDlt
                                        ? `₹${child.amtPaidForDlt.toLocaleString("en-IN")}`
                                        : "-")}
                                  </Typography>
                                </Stack>

                                {child.dltAccount && (
                                  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                                    <Typography variant="label">Actions</Typography>
                                    {renderChildActions?.(row, child)}
                                  </Stack>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

